// components/etatSalaires/OuvertureFenetreDialog.jsx
// ============================================================================
// OUVERTURE EXCEPTIONNELLE D'UNE ÉTAPE DU CALENDRIER DE PAIE
// ============================================================================
// Deux usages :
//   - mode « demander » : rouvrir une étape (avances, demande de dotation,
//     paiement) hors de ses dates, pour le mois en cours, jusqu'à une date. La
//     demande part au Centre de Support ;
//   - mode « refermer » : fermer une ouverture accordée avant son échéance.
// Le serveur tranche (backend2/services/OuvertureFenetrePaieService.js).
// ============================================================================

import React, { useState } from 'react';
import { Loader2, Unlock, Lock, Info } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../ui/dialog';
import { getAuthHeaders } from '../../services/api';
import { usePermissionsAnnulationPrivilegiee } from '../avances/annulationPrivilegieeApi';

const CHAMP = 'h-9 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-800 outline-none transition focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15';
const ZONE_TEXTE = 'w-full resize-none rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15';
const BOUTON_PIED = 'inline-flex h-9 min-w-[168px] items-center justify-center gap-2 rounded-md px-4 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50';
const BOUTON_SECONDAIRE = `${BOUTON_PIED} border border-gray-300 bg-white text-gray-700 hover:bg-gray-50`;
const BOUTON_PRINCIPAL = `${BOUTON_PIED} bg-[#246990] text-white hover:bg-[#1f5a7a]`;

const appeler = async (url, corps) => {
  const response = await fetch(url, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(corps) });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.success === false) throw new Error(data.message || `Erreur ${response.status}`);
  return data;
};

const aujourdHui = () => {
  const d = new Date();
  return [d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'), String(d.getDate()).padStart(2, '0')].join('-');
};

const OuvertureFenetreDialog = ({ mode = 'demander', etape, mois, ouverture, open, onClose, onTermine }) => {
  const { estSuperValidateur } = usePermissionsAnnulationPrivilegiee();
  const refermer = mode === 'refermer';
  const minimum = refermer ? 5 : (estSuperValidateur ? 30 : 10);
  const finMois = String(mois?.date_fin || '').slice(0, 10);

  const [dateFin, setDateFin] = useState(finMois);
  const [motif, setMotif] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');

  const peutEnvoyer = motif.trim().length >= minimum && (refermer || Boolean(dateFin)) && !envoi;

  const envoyer = async () => {
    if (!peutEnvoyer) return;
    setEnvoi(true);
    setErreur('');
    try {
      const resultat = refermer
        ? await appeler(`/api/etat-salaires/ouvertures/${ouverture.id}/revoquer`, { motif: motif.trim() })
        : await appeler('/api/etat-salaires/ouvertures', {
          mois_scolaire_id: mois.id,
          etape: etape.cle,
          date_fin: dateFin,
          motif: motif.trim()
        });
      onTermine?.(resultat);
    } catch (e) {
      setErreur(e.message);
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(ouvert) => { if (!ouvert && !envoi) onClose?.(); }}>
      <DialogContent className="w-[95vw] max-w-2xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0">
        <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-5 py-4 text-left">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#246990] text-white">
            {refermer ? <Lock size={18} /> : <Unlock size={18} />}
          </div>
          <div className="min-w-0">
            <DialogTitle className="text-lg font-semibold text-gray-900">
              {refermer ? "Refermer l'ouverture exceptionnelle" : 'Demander une ouverture exceptionnelle'}
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-500">
              {etape?.libelle} · mois de paie de {mois?.nom}
            </DialogDescription>
          </div>
        </DialogHeader>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {erreur && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{erreur}</div>}

          {refermer ? (
            <p className="text-sm text-gray-600">
              L'étape est ouverte jusqu'au <span className="font-semibold text-gray-900">{ouverture?.jusqu_au}</span>. La
              refermer rétablit tout de suite les dates du calendrier ; l'ouverture reste dans l'historique.
            </p>
          ) : (
            <>
              <p className="text-sm text-gray-600">
                Actuellement : <span className="font-semibold text-gray-900">{etape?.dates}</span>. L'ouverture autorise
                l'étape du jour de la validation jusqu'à la date choisie, au plus tard la fin du mois de paie. Les autres
                contrôles restent appliqués (mois figé, pointages, dotation acceptée, salaire déjà payé).
              </p>
              <label className="block max-w-xs">
                <span className="mb-1 block text-sm font-medium text-gray-700">Ouverte jusqu'au</span>
                <input
                  type="date"
                  value={dateFin}
                  min={aujourdHui()}
                  max={finMois || undefined}
                  onChange={(e) => setDateFin(e.target.value)}
                  className={CHAMP}
                />
              </label>
            </>
          )}

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">
              {refermer ? 'Pourquoi la refermer' : 'Motif'}
            </span>
            <textarea
              rows={4}
              value={motif}
              onChange={(e) => setMotif(e.target.value)}
              placeholder={refermer ? "L'opération prévue a été faite..." : "Pourquoi l'opération ne peut pas attendre ou être faite dans les dates..."}
              className={ZONE_TEXTE}
            />
            <span className="mt-1 block text-[11px] text-gray-500">
              {minimum} caractères au moins ({motif.trim().length} saisis).
            </span>
          </label>

          {!refermer && (
            <div className="flex items-start gap-2 rounded-lg border border-[#246990]/20 bg-[#eef7fb] p-3 text-sm text-[#246990]">
              <Info size={15} className="mt-0.5 shrink-0" />
              <span>
                {estSuperValidateur
                  ? 'La demande part au Centre de Support. En tant que super validateur, vous pourrez la valider vous-même avec votre code de double authentification.'
                  : 'La demande part au Centre de Support : une autre personne la valide.'}
              </span>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 border-t bg-gray-50 px-5 py-4 sm:space-x-0">
          <button type="button" onClick={onClose} disabled={envoi} className={BOUTON_SECONDAIRE}>Annuler</button>
          <button type="button" onClick={envoyer} disabled={!peutEnvoyer} className={BOUTON_PRINCIPAL}>
            {envoi ? <Loader2 className="h-4 w-4 animate-spin" /> : (refermer ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />)}
            {refermer ? 'Refermer' : 'Envoyer la demande'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default OuvertureFenetreDialog;
